import React from 'react';
import {
    ChevronDown,
    ChevronUp,
    FileText,
    Link,
    ExternalLink,
    Eye,
    Download,
    CheckCircle,
    Clock,
    Calendar
} from 'lucide-react';

const ResourceCard = ({ resource, isExpanded, onToggle, onViewResource, onMarkAsCompleted, currentUserId, isMarking }) => {

    const getResourceIcon = () => {
        switch (resource?.type) {
            case 'document':
                return <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-custom-blue-1" />;
            case 'link':
                return <Link className="w-4 h-4 sm:w-5 sm:h-5 text-custom-blue-1" />;
            case 'event':
                return <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />;
            default:
                return <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-custom-blue-1" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getResourceUrl = () => {
        return resource?.resourceUrl || resource?.fileUrl || '';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        let date;

        if (timestamp.toDate) {
            // Firestore Timestamp object
            date = timestamp.toDate();
        } else if (typeof timestamp.seconds === "number") {
            // Plain object from Firestore (seconds + nanoseconds)
            date = new Date(timestamp.seconds * 1000);
        } else {
            // Try to construct Date normally
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) return "N/A";

        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };


    const handleViewResource = () => {
        if (onViewResource && getResourceUrl()) {
            onViewResource(resource);
        }
    };

    const handleOpenInNewTab = () => {
        const url = getResourceUrl();
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleDownloadFile = () => {
        const url = resource?.fileUrl || resource?.resourceUrl;
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = resource?.fileName || resource?.title || 'resource';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleMarkAsCompleted = () => {
        if (onMarkAsCompleted && currentUserId) {
            onMarkAsCompleted(resource.id);
        }
    };

    // Check if current user has already read this resource
    const isAlreadyRead = resource?.readBy?.includes(currentUserId) || false;

    return (
        <div className="bg-white rounded-lg border shadow-sm">
            <div
                className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={onToggle}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-custom-blue-1/10
                            }`}>
                            {getResourceIcon()}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                {resource?.title?.trim() || "N/A"}
                            </h4>
                            <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                                <span className="truncate">
                                    {resource?.instructor ?? 'N/A'}
                                    {resource?.isAdminResource && ' (Administrator)'}
                                </span>
                                {resource?.fileSize && (
                                    <>
                                        <span className="hidden sm:inline">•</span>
                                        <span className="hidden sm:inline">{formatFileSize(resource.fileSize)}</span>
                                    </>
                                )}
                                {resource?.uploadedAt && (
                                    <>
                                        <span className="hidden sm:inline">•</span>
                                        <span className="hidden sm:inline">{formatDate(resource.uploadedAt)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {isAlreadyRead && (
                            <span className="flex items-center gap-1 text-xs px-1 sm:px-2 py-1 rounded-full bg-custom-green-1/10 text-custom-green-1">
                                <CheckCircle className="w-3 h-3" />
                                <span className="hidden sm:inline">Completed</span>
                            </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${resource?.status === 'published' ?
                            'bg-custom-green-1/10 text-custom-green-1' :
                            'bg-gray-100 text-gray-600'
                            }`}>
                            {resource?.status || 'pending'}
                        </span>
                        {isExpanded ? <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" /> : <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-gray-100">
                    <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">{resource?.description}</p>
                    <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                        {resource?.type === 'document' && resource?.fileUrl && (
                            <button
                                onClick={handleViewResource}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 transition-colors text-sm sm:text-base"
                            >
                                <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">View Document</span>
                                <span className="sm:hidden">View</span>
                            </button>
                        )}
                        {resource?.type === 'document' && resource?.fileUrl && (
                            <button
                                onClick={handleDownloadFile}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
                            >
                                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Download</span>
                                <span className="sm:hidden">DL</span>
                            </button>
                        )}
                        {resource?.type === 'link' && (
                            <button
                                onClick={handleOpenInNewTab}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
                            >
                                <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Open Link</span>
                                <span className="sm:hidden">Open</span>
                            </button>
                        )}
                        {resource?.type === 'event' && resource?.eventUrl && (
                            <button
                                onClick={() => window.open(resource.eventUrl, '_blank')}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
                            >
                                <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Join Event</span>
                                <span className="sm:hidden">Join</span>
                            </button>
                        )}
                        {!isAlreadyRead && (
                            <button
                                onClick={handleMarkAsCompleted}
                                disabled={isMarking}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-custom-green-1 text-white rounded-lg hover:bg-custom-green-1/90 transition-colors disabled:opacity-50 text-sm sm:text-base"
                            >
                                {isMarking ? (
                                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                                ) : (
                                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                )}
                                <span className="hidden sm:inline">Mark Complete</span>
                                <span className="sm:hidden">Done</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourceCard;